import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { FiSearch, FiX, FiEye, FiDollarSign } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtFecha, localDate } from '../utils/format';
import '../styles/Compras.css';
const fmt = (n) => parseFloat(n || 0).toFixed(2);
const today = () => localDate();

const isVencida = (row) =>
  ['PENDIENTE', 'PARCIAL'].includes(row.estado) &&
  row.fecha_vencimiento &&
  String(row.fecha_vencimiento).slice(0, 10) < today();

export default function CuentasPagarPage() {
  const { user } = useAuth();
  const bizId = user?.business_id || 1;
  /* ── listado ── */
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [summary,    setSummary]    = useState({});
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  /* ── filtros ── */
  const [search,       setSearch]       = useState('');
  const [fDesde,       setFDesde]       = useState('');
  const [fHasta,       setFHasta]       = useState('');
  const [fEstado,      setFEstado]      = useState('');
  const [fProv,        setFProv]        = useState('');
  const [soloVencidas, setSoloVencidas] = useState(false);

  /* ── proveedores para filtro ── */
  const [proveedores, setProveedores] = useState([]);

  /* ── modal detalle/pago ── */
  const [modalCxP,     setModalCxP]    = useState(null);
  const [formasPago,   setFormasPago]  = useState([]);
  const [pagoForm,     setPagoForm]    = useState({ forma_pago: '', monto: '', referencia: '', fecha: localDate() });
  const [paying,       setPaying]      = useState(false);
  const [pagoError,    setPagoError]   = useState('');
  const [showPagoForm, setShowPagoForm] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/api/cuentas-pagar/summary');
      setSummary(r.data.data || {});
    } catch {}
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/cuentas-pagar', {
        params: {
          q: search, from_date: fDesde, to_date: fHasta,
          estado: fEstado, proveedor_id: fProv,
          solo_vencidas: soloVencidas ? 'true' : ''
        }
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, fDesde, fHasta, fEstado, fProv, soloVencidas]);

  useEffect(() => { loadList(); loadSummary(); }, [loadList, loadSummary]);

  useEffect(() => {
    api.get('/api/proveedores').then(r => setProveedores(r.data.data || []));
    api.get(`/api/config/formas-pago?business_id=${bizId}`).then(r => setFormasPago(r.data.data || [])).catch(() => {});
  }, []);

  const openDetalle = async (id) => {
    setPagoError(''); setShowPagoForm(false);
    setPagoForm({ forma_pago: '', monto: '', referencia: '', fecha: localDate() });
    try {
      const r = await api.get(`/api/cuentas-pagar/${id}`);
      setModalCxP(r.data.data);
    } catch (e) {
      showToast(e.response?.data?.error || e.message);
    }
  };

  const handlePagar = async () => {
    if (!pagoForm.monto || parseFloat(pagoForm.monto) <= 0) {
      setPagoError('Ingrese un monto válido'); return;
    }
    setPaying(true); setPagoError('');
    try {
      await api.post(`/api/cuentas-pagar/${modalCxP.id}/pagar`, pagoForm);
      // Reload detalle
      const r = await api.get(`/api/cuentas-pagar/${modalCxP.id}`);
      setModalCxP(r.data.data);
      setShowPagoForm(false);
      setPagoForm({ forma_pago: '', monto: '', referencia: '', fecha: localDate() });
      loadList();
      loadSummary();
    } catch (e) {
      setPagoError(e.response?.data?.error || e.message);
    } finally {
      setPaying(false);
    }
  };

  const restante = modalCxP
    ? parseFloat(modalCxP.monto || 0) - parseFloat(modalCxP.monto_pagado || 0)
    : 0;

  const estadoBadgeClass = (e, row) => {
    if (isVencida(row)) return 'cxp-vencida';
    const map = { PENDIENTE: 'cxp-pendiente', PARCIAL: 'cxp-parcial', PAGADO: 'cxp-pagado', ANULADO: 'cxp-anulado' };
    return map[e] || 'cxp-pendiente';
  };
  const estadoBadgeLabel = (e, row) => {
    if (isVencida(row)) return 'VENCIDA';
    return e;
  };

  return (
    <div className="cmp-page">

      {/* ── KPIs ── */}
      <div className="cmp-kpis">
        <div className="cmp-kpi-card pendiente">
          <div className="cmp-kpi-label">Total Pendiente</div>
          <div className={`cmp-kpi-value ${parseFloat(summary.total_pendiente) > 0 ? 'warning' : ''}`}>
            S/ {fmt(summary.total_pendiente)}
          </div>
        </div>
        <div className="cmp-kpi-card vencido">
          <div className="cmp-kpi-label">Deuda Vencida</div>
          <div className={`cmp-kpi-value ${parseFloat(summary.total_vencido) > 0 ? 'danger' : ''}`}>
            S/ {fmt(summary.total_vencido)}
          </div>
        </div>
        <div className="cmp-kpi-card count">
          <div className="cmp-kpi-label">Cuentas Pendientes</div>
          <div className="cmp-kpi-value purple">{summary.count_pendiente || 0}</div>
        </div>
        <div className="cmp-kpi-card abonado">
          <div className="cmp-kpi-label">Total Abonado</div>
          <div className="cmp-kpi-value success">S/ {fmt(summary.total_abonado)}</div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="cmp-toolbar" style={{ marginTop: 12 }}>
        <div className="cmp-search-box">
          <FiSearch size={13} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Proveedor, N° compra, RUC…"
          />
        </div>

        <input type="date" className="cmp-filter-date" value={fDesde} onChange={e => setFDesde(e.target.value)} />
        <span className="cmp-filter-sep">—</span>
        <input type="date" className="cmp-filter-date" value={fHasta} onChange={e => setFHasta(e.target.value)} />

        <select className="cmp-filter-select" value={fEstado} onChange={e => setFEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="PARCIAL">Parcial</option>
          <option value="PAGADO">Pagado</option>
        </select>

        <select className="cmp-filter-select" value={fProv} onChange={e => setFProv(e.target.value)}>
          <option value="">Todos los proveedores</option>
          {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={soloVencidas}
            onChange={e => setSoloVencidas(e.target.checked)}
          />
          Solo vencidas
        </label>

        {(search || fDesde || fHasta || fEstado || fProv || soloVencidas) && (
          <button className="cmp-btn-clear" onClick={() => {
            setSearch(''); setFDesde(''); setFHasta('');
            setFEstado(''); setFProv(''); setSoloVencidas(false);
          }}>
            <FiX size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* ── Tabla ── */}
      <div className="cmp-table-wrap">
        {loading ? (
          <div className="cmp-empty">Cargando…</div>
        ) : error ? (
          <div className="cmp-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="cmp-empty">No hay cuentas por pagar</div>
        ) : (
          <table className="cmp-table">
            <thead>
              <tr>
                <th>N° Compra</th>
                <th>Fecha Compra</th>
                <th>Proveedor</th>
                <th>Tipo Doc.</th>
                <th>Condición</th>
                <th>Vencimiento</th>
                <th style={{ textAlign: 'right' }}>Monto</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th style={{ textAlign: 'right' }}>Restante</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td><strong>{r.compra_numero}</strong></td>
                  <td>{fmtFecha(r.compra_fecha)}</td>
                  <td>
                    <div>{r.proveedor_nombre}</div>
                    {r.proveedor_ruc && <small style={{ color: '#64748b' }}>{r.proveedor_ruc}</small>}
                  </td>
                  <td>{r.tipo_documento}</td>
                  <td>
                    <span className={`cmp-badge ${r.condicion_pago === 'CONTADO' ? 'contado' : 'credito'}`}>
                      {r.condicion_pago}
                    </span>
                  </td>
                  <td style={{ color: isVencida(r) ? '#ef4444' : undefined, fontWeight: isVencida(r) ? 600 : undefined }}>
                    {fmtFecha(r.fecha_vencimiento)}
                  </td>
                  <td style={{ textAlign: 'right' }}>S/ {fmt(r.monto)}</td>
                  <td style={{ textAlign: 'right', color: '#15803d' }}>S/ {fmt(r.monto_pagado)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: parseFloat(r.restante) > 0 ? '#ef4444' : '#94a3b8' }}>
                    S/ {fmt(r.restante)}
                  </td>
                  <td>
                    <span className={`cmp-badge ${estadoBadgeClass(r.estado, r)}`}>
                      {estadoBadgeLabel(r.estado, r)}
                    </span>
                  </td>
                  <td>
                    <div className="ia-actions">
                      <button className="ia-btn ia-btn--view" onClick={() => openDetalle(r.id)} title="Ver detalle">
                        <FiEye size={15} />
                      </button>
                      {['PENDIENTE', 'PARCIAL'].includes(r.estado) && (
                        <button className="ia-btn ia-btn--complete" title="Abonar" onClick={() => {
                          openDetalle(r.id).then(() => setShowPagoForm(true));
                        }}>
                          <FiDollarSign size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* totalizador */}
      <div className="cmp-total-bar">
        <span>{total} cuenta{total !== 1 ? 's' : ''}</span>
        <span>Mostrando: <strong>{rows.length}</strong></span>
      </div>

      {/* ── MODAL DETALLE / PAGO ── */}
      {modalCxP && (
        <div className="cmp-overlay">
          <div className="cmp-modal">
            <div className="cmp-modal-head">
              <h3>Cuenta por Pagar — {modalCxP.compra_numero}</h3>
              <button className="cmp-modal-close" onClick={() => setModalCxP(null)}>×</button>
            </div>

            {/* Saldo bar */}
            <div className="cmp-saldo-bar">
              <div className="saldo-item">
                <span className="saldo-label">Monto Total</span>
                <span className="saldo-value">S/ {fmt(modalCxP.monto)}</span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Pagado</span>
                <span className="saldo-value success">S/ {fmt(modalCxP.monto_pagado)}</span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Restante</span>
                <span className={`saldo-value ${restante > 0 ? 'danger' : ''}`}>S/ {fmt(restante)}</span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Vencimiento</span>
                <span className={`saldo-value ${isVencida(modalCxP) ? 'danger' : ''}`}>
                  {fmtFecha(modalCxP.fecha_vencimiento)}
                </span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Estado</span>
                <span className={`cmp-badge cxp-${modalCxP.estado.toLowerCase()}`}>
                  {isVencida(modalCxP) ? 'VENCIDA' : modalCxP.estado}
                </span>
              </div>
            </div>

            <div className="cmp-modal-body">
              {/* Info */}
              <div className="cmp-detail-section">
                <h4>Proveedor</h4>
                <div className="cmp-detail-grid">
                  <span className="key">Nombre</span>
                  <span className="val">{modalCxP.proveedor_nombre}</span>
                  {modalCxP.proveedor_ruc && <>
                    <span className="key">RUC</span>
                    <span className="val">{modalCxP.proveedor_ruc}</span>
                  </>}
                  <span className="key">Compra</span>
                  <span className="val">{modalCxP.compra_numero} — {fmtFecha(modalCxP.compra_fecha)}</span>
                  <span className="key">Documento</span>
                  <span className="val">
                    {modalCxP.tipo_documento}
                    {[modalCxP.serie, modalCxP.nro_documento].filter(Boolean).join('-')
                      ? ` — ${[modalCxP.serie, modalCxP.nro_documento].filter(Boolean).join('-')}`
                      : ''}
                  </span>
                </div>
              </div>

              {/* Historial de pagos */}
              <div className="cmp-detail-section">
                <h4>Historial de pagos</h4>
                {(modalCxP.pagos || []).length === 0 ? (
                  <div className="cmp-empty" style={{ padding: '12px 0' }}>Sin abonos registrados</div>
                ) : (
                  <div className="cmp-pagos-list">
                    {modalCxP.pagos.map(p => (
                      <div key={p.id} className="cmp-pago-item">
                        <div className="pago-left">
                          <span className="pago-fecha">{fmtFecha(p.fecha || p.pagado_en)}</span>
                          <span className="pago-forma">{p.forma_pago || 'Sin especificar'}</span>
                          {p.referencia && <span className="pago-ref">Ref: {p.referencia}</span>}
                        </div>
                        <span className="pago-monto">S/ {fmt(p.monto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Formulario de pago */}
              {['PENDIENTE', 'PARCIAL'].includes(modalCxP.estado) && (
                <>
                  {!showPagoForm ? (
                    <button className="cmp-btn-pagar-modal" onClick={() => setShowPagoForm(true)}>
                      <FiDollarSign size={13} style={{ marginRight: 5 }} />
                      Registrar Abono
                    </button>
                  ) : (
                    <div className="cmp-detail-section">
                      <h4>Nuevo Abono</h4>
                      {pagoError && <div className="cmp-error">{pagoError}</div>}
                      <div className="cmp-form-grid">
                        <div className="cmp-field">
                          <label>Forma de Pago</label>
                          <select
                            value={pagoForm.forma_pago}
                            onChange={e => setPagoForm(p => ({ ...p, forma_pago: e.target.value }))}
                          >
                            <option value="">— Seleccionar —</option>
                            {formasPago.filter(fp => fp.name).map(fp => (
                              <option key={fp.id} value={fp.name}>{fp.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="cmp-field">
                          <label>Fecha del Abono</label>
                          <input
                            type="date"
                            value={pagoForm.fecha}
                            onChange={e => setPagoForm(p => ({ ...p, fecha: e.target.value }))}
                          />
                        </div>
                        <div className="cmp-field">
                          <label>Monto (Restante: S/ {fmt(restante)})</label>
                          <input
                            type="number" min="0.01" step="0.01"
                            max={restante}
                            value={pagoForm.monto}
                            onChange={e => setPagoForm(p => ({ ...p, monto: e.target.value }))}
                            placeholder={fmt(restante)}
                          />
                        </div>
                        <div className="cmp-field cmp-form-col-full">
                          <label>Referencia / N° Operación</label>
                          <input
                            value={pagoForm.referencia}
                            onChange={e => setPagoForm(p => ({ ...p, referencia: e.target.value }))}
                            placeholder="Opcional…"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="cmp-modal-foot">
              <button className="cmp-btn-cancel" onClick={() => setModalCxP(null)}>Cerrar</button>
              {showPagoForm && ['PENDIENTE', 'PARCIAL'].includes(modalCxP.estado) && (
                <button className="cmp-btn-pagar-modal" onClick={handlePagar} disabled={paying}>
                  {paying ? 'Registrando…' : 'Confirmar Abono'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
